/**
 * MarqueeOverlay Component
 *
 * Visual overlay for drag-to-select (marquee/lasso) selection.
 * Renders a translucent rectangle with animated border showing the current selection area.
 *
 * Features:
 * - Smooth animations on appear/disappear
 * - Primary color scheme for brand consistency
 * - Accessible with proper ARIA labels
 * - Hardware-accelerated rendering for 60fps
 *
 * Week 3: Selection System implementation
 */

import React from 'react';
import { cn } from '@/lib/utils';
import type { MarqueeRectangle } from '@/hooks/editor/useMarqueeSelection';
import {
  FOREST_GREEN,
  SAGE_GREEN,
  DESERT_SUN,
} from '@/styles/tokens';

interface MarqueeOverlayProps {
  /**
   * The marquee rectangle to render
   * If null, the overlay will not render
   */
  rectangle: MarqueeRectangle | null;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Color scheme variant
   * Default: 'primary'
   */
  variant?: 'primary' | 'success' | 'warning';
}

/**
 * MarqueeOverlay Component
 *
 * @example
 * ```typescript
 * function EditorCanvas() {
 *   const { marqueeRectangle, isActive } = useMarqueeSelection({
 *     enabled: true,
 *     canvasRef,
 *     scale,
 *   });
 *
 *   // Render the marquee overlay when active
 *   // Use: <MarqueeOverlay rectangle={marqueeRectangle} />
 * }
 * ```
 */
export const MarqueeOverlay: React.FC<MarqueeOverlayProps> = ({
  rectangle,
  className,
  variant = 'primary',
}) => {
  if (!rectangle || rectangle.width === 0 || rectangle.height === 0) {
    return null;
  }

  // Color schemes for different variants using design tokens
  const colorSchemes = {
    primary: {
      bg: `${FOREST_GREEN.DEFAULT}1A`, // 10% opacity
      borderColor: FOREST_GREEN.DEFAULT,
    },
    success: {
      bg: `${SAGE_GREEN.DEFAULT}1A`, // 10% opacity
      borderColor: SAGE_GREEN.DEFAULT,
    },
    warning: {
      bg: `${DESERT_SUN.DEFAULT}1A`, // 10% opacity
      borderColor: DESERT_SUN.DEFAULT,
    },
  };

  const colors = colorSchemes[variant];

  return (
    <div
      className={cn(
        'absolute pointer-events-none z-[999]',
        'border-2 rounded',
        // Smooth fade-in animation
        'animate-in fade-in-0 duration-100',
        // Hardware acceleration
        'will-change-transform',
        className
      )}
      style={{
        left: `${rectangle.x}px`,
        top: `${rectangle.y}px`,
        width: `${rectangle.width}px`,
        height: `${rectangle.height}px`,
        // Enhanced border with animated dashes
        borderStyle: 'dashed',
        borderWidth: '2px',
        borderColor: colors.borderColor,
        backgroundColor: colors.bg,
        // Subtle glow effect
        boxShadow: `0 0 12px ${colors.borderColor}40, inset 0 0 12px ${colors.borderColor}20`,
      }}
      role="region"
      aria-label={`Selection area: ${Math.round(rectangle.width)} × ${Math.round(rectangle.height)} pixels`}
      aria-live="polite"
    >
      {/* Corner indicators for better visibility */}
      <div
        className="absolute -top-1 -left-1 w-2 h-2 rounded-full"
        style={{
          backgroundColor: colors.borderColor,
          boxShadow: `0 0 4px ${colors.borderColor}`,
        }}
      />
      <div
        className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
        style={{
          backgroundColor: colors.borderColor,
          boxShadow: `0 0 4px ${colors.borderColor}`,
        }}
      />
      <div
        className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full"
        style={{
          backgroundColor: colors.borderColor,
          boxShadow: `0 0 4px ${colors.borderColor}`,
        }}
      />
      <div
        className="absolute -bottom-1 -right-1 w-2 h-2 rounded-full"
        style={{
          backgroundColor: colors.borderColor,
          boxShadow: `0 0 4px ${colors.borderColor}`,
        }}
      />

      {/* Dimensions tooltip */}
      {rectangle.width > 50 && rectangle.height > 30 && (
        <div
          className={cn(
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'px-2 py-1 rounded text-xs font-semibold',
            'text-white backdrop-blur-sm',
            'pointer-events-none',
            'animate-in fade-in-0 zoom-in-95 duration-200 delay-100'
          )}
          style={{
            backgroundColor: `${colors.borderColor}E6`, // 90% opacity
            boxShadow: `0 2px 8px ${colors.borderColor}40`,
          }}
        >
          {Math.round(rectangle.width)} × {Math.round(rectangle.height)}
        </div>
      )}
    </div>
  );
};

/**
 * Memoized version for performance optimization
 * Prevents unnecessary re-renders when parent components update
 */
export const MemoizedMarqueeOverlay = React.memo(MarqueeOverlay);
